import { API_BASE_URL } from '@synergy/config';
import { request } from '@synergy/api';
import { useEffect, useState } from 'react';

/**
 * How many locked records a search would find that the list is not showing.
 *
 * A list hides locked partners (LST-S14), which makes a search for one of them
 * come back empty — and the next thing a user does with an empty search is
 * create the partner a second time. The empty state therefore says how many
 * locked records match, and the number has to be asked for separately: the
 * list itself asked for the unlocked ones.
 *
 * Only asked while the list stands empty, so the usual case costs nothing.
 */
export const usePaloLockedCount = (path: string, params: URLSearchParams, enabled: boolean): number => {
    const [count, setCount] = useState(0);
    const query = params.toString();

    useEffect(() => {
        if (!enabled) {
            setCount(0);

            return undefined;
        }

        let cancelled = false;

        const ask = async (): Promise<void> => {
            const next = new URLSearchParams(query);

            next.set('isLocked', 'true');
            next.set('page', '1');
            next.set('limit', '1');

            try {
                const response = await request.get(`${API_BASE_URL}${path}`, { params: next });

                if (!cancelled) {
                    setCount(response?.data?.total ?? 0);
                }
            } catch {
                // An empty list that cannot count the locked ones is still an
                // empty list; it just says nothing about them.
                if (!cancelled) {
                    setCount(0);
                }
            }
        };

        ask();

        return () => {
            cancelled = true;
        };
    }, [path, query, enabled]);

    return count;
};
